import React, { useState } from 'react';
import { BaseConfigProps, commonStyles } from '../shared/ConfigFormHelpers';
import CollapsibleSection from '../shared/CollapsibleSection';

const ExcelProcessorConfig: React.FC<BaseConfigProps> = ({ node, updateConfig }) => {
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(['basic', 'operation']));
  
  const toggleSection = (section: string) => {
    setExpandedSections(prev => {
      const newSet = new Set(prev);
      if (newSet.has(section)) {
        newSet.delete(section);
      } else {
        newSet.add(section);
      }
      return newSet;
    });
  };

  const operations = [
    { value: 'read', label: 'Olvasás', icon: '📖', desc: 'Excel fájl beolvasása' },
    { value: 'write', label: 'Írás', icon: '✍️', desc: 'Új Excel fájl létrehozása' },
    { value: 'append', label: 'Hozzáfűzés', icon: '➕', desc: 'Sorok hozzáadása' },
    { value: 'update', label: 'Frissítés', icon: '🔄', desc: 'Meglévő sorok módosítása' },
  ];

  return (
    <div className="space-y-3">
      {/* Művelet típus */}
      <CollapsibleSection
        id="operation"
        title="Művelet Típusa"
        icon="📊"
        color="green"
        isExpanded={expandedSections.has('operation')}
        onToggle={toggleSection}
      >
        <div className="space-y-3">
          <div className="grid grid-cols-1 gap-2">
            {operations.map((op) => (
              <button
                key={op.value}
                onClick={() => updateConfig('operation', op.value)}
                className={`flex items-center p-3 rounded-md border text-sm transition-all ${
                  (node.data.config.operation || 'read') === op.value
                    ? 'border-green-500 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300'
                    : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:border-gray-400'
                }`}
              >
                <span className="text-xl mr-3">{op.icon}</span>
                <div className="flex-1 text-left">
                  <div className="font-medium">{op.label}</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">{op.desc}</div>
                </div>
                {(node.data.config.operation || 'read') === op.value && (
                  <span className="text-green-500">✓</span>
                )}
              </button>
            ))}
          </div>
        </div>
      </CollapsibleSection>

      {/* Alapbeállítások */}
      <CollapsibleSection
        id="basic"
        title="Excel Beállítások"
        icon="📗"
        color="emerald"
        isExpanded={expandedSections.has('basic')}
        onToggle={toggleSection}
      >
        <div className="space-y-3">
          {(node.data.config.operation === 'read' || !node.data.config.operation) && (
            <div>
              <label className={commonStyles.label}>
                Excel fájl
              </label>
              <input
                type="text"
                value={node.data.config.file || ''}
                onChange={(e) => updateConfig('file', e.target.value)}
                className={commonStyles.input}
                placeholder="/path/to/data.xlsx vagy {{file_path}}"
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Fájl útvonal vagy változó
              </p>
            </div>
          )}

          <div>
            <label className={commonStyles.label}>
              Munkalap neve
            </label>
            <input
              type="text"
              value={node.data.config.sheet || 'Sheet1'}
              onChange={(e) => updateConfig('sheet', e.target.value)}
              className={commonStyles.input}
              placeholder="Sheet1"
            />
          </div>

          {(node.data.config.operation === 'read' || !node.data.config.operation) && (
            <>
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="hasHeader"
                  checked={node.data.config.hasHeader !== false}
                  onChange={(e) => updateConfig('hasHeader', e.target.checked)}
                  className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                />
                <label htmlFor="hasHeader" className="text-sm text-gray-700 dark:text-gray-300">
                  Első sor fejléc
                </label>
              </div>

              <div>
                <label className={commonStyles.label}>
                  Kezdő sor (opcionális)
                </label>
                <input
                  type="number"
                  value={node.data.config.startRow || ''}
                  onChange={(e) => updateConfig('startRow', parseInt(e.target.value) || null)}
                  className={commonStyles.input}
                  placeholder="1"
                  min="1"
                />
              </div>
            </>
          )}
        </div>
      </CollapsibleSection>

      {/* Adatok */}
      {node.data.config.operation !== 'read' && (
        <CollapsibleSection
          id="data"
          title="Adatok"
          icon="📋"
          color="blue"
          isExpanded={expandedSections.has('data')}
          onToggle={toggleSection}
        >
          <div className="space-y-3">
            <div>
              <label className={commonStyles.label}>
                Adat forrás
              </label>
              <textarea
                value={node.data.config.data || ''}
                onChange={(e) => updateConfig('data', e.target.value)}
                className={commonStyles.textarea}
                rows={4}
                placeholder="{{data}} vagy JSON formátumú adatok"
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Változó vagy JSON array
              </p>
            </div>

            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md p-3">
              <div className="text-xs text-blue-700 dark:text-blue-300">
                <strong>💡 Formátum:</strong> <code className="bg-blue-100 dark:bg-blue-800 px-1 rounded">[{'{'}name: "John", age: 30{'}'}]</code>
              </div>
            </div>
          </div>
        </CollapsibleSection>
      )}

      {/* Kimenet Változók */}
      <CollapsibleSection
        id="variables"
        title="Kimenet Változók"
        icon="🔗"
        color="purple"
        isExpanded={expandedSections.has('variables')}
        onToggle={toggleSection}
      >
        <div className="space-y-2">
          <div className="text-xs text-gray-600 dark:text-gray-400 mb-3">
            A művelet után ezek a változók lesznek elérhetők:
          </div>
          
          <div className="grid grid-cols-1 gap-2">
            {((node.data.config.operation === 'read' || !node.data.config.operation) ? [
              { var: '{{data}}', desc: 'Excel adatok (array)', example: '[{...}, {...}]' },
              { var: '{{rows}}', desc: 'Sorok száma', example: '150' },
              { var: '{{columns}}', desc: 'Oszlopok', example: '["Name", "Age"]' }
            ] : [
              { var: '{{success}}', desc: 'Sikeres művelet', example: 'true' },
              { var: '{{file_path}}', desc: 'Fájl útvonal', example: '/output.xlsx' },
              { var: '{{rows_written}}', desc: 'Írt sorok', example: '50' }
            ]).map((item) => (
              <div key={item.var} className="bg-gray-50 dark:bg-gray-800 rounded-md p-2 border border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between">
                  <code className="text-xs font-mono text-purple-600 dark:text-purple-400 bg-purple-50 dark:bg-purple-900/20 px-2 py-1 rounded">
                    {item.var}
                  </code>
                  <span className="text-xs text-gray-500 dark:text-gray-400 font-mono">
                    {item.example}
                  </span>
                </div>
                <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                  {item.desc}
                </div>
              </div>
            ))}
          </div>
        </div>
      </CollapsibleSection>

      {/* Példák */}
      <CollapsibleSection
        id="examples"
        title="Használati Példák"
        icon="💡"
        color="amber"
        isExpanded={expandedSections.has('examples')}
        onToggle={toggleSection}
      >
        <div className="space-y-2 text-xs text-gray-600 dark:text-gray-400">
          <div className="bg-gray-50 dark:bg-gray-800 p-2 rounded border border-gray-200 dark:border-gray-700">
            <strong>📊 Adatok importálása:</strong>
            <div className="mt-1 font-mono text-[10px]">
              Excel olvasás → Adatok szűrése → CRM import
            </div>
          </div>
          <div className="bg-gray-50 dark:bg-gray-800 p-2 rounded border border-gray-200 dark:border-gray-700">
            <strong>📈 Report exportálás:</strong>
            <div className="mt-1 font-mono text-[10px]">
              CRM lekérdezés → Excel írás → Email melléklet
            </div>
          </div>
          <div className="bg-gray-50 dark:bg-gray-800 p-2 rounded border border-gray-200 dark:border-gray-700">
            <strong>🔄 Adatok frissítése:</strong>
            <div className="mt-1 font-mono text-[10px]">
              Excel olvasás → Feldolgozás → Excel frissítés
            </div>
          </div>
        </div>
      </CollapsibleSection>
    </div>
  );
};

export default ExcelProcessorConfig;
